import {
  DEFAULT_DRILL_RULES,
  type DrillTemplateContent,
  parseCompanyDrillRule,
  parseDrillConduct,
  parseDrillPlan,
  parseDrillPostponement,
  parseDrillVariant,
  type SchemaIssue,
} from '@felix/domain';
import {
  Alert,
  Button,
  Drawer,
  Modal,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  TagsInput,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { formatDateTime, formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { useShell } from '../../shell-context.js';
import table from '../pms/Pms.module.css';
import work from '../pms/PmsWork.module.css';
import classes from './DrillPlanner.module.css';
import { DrillStatusBadge, ReviewBadge, useDrillErrorText } from './DrillPlanParts.js';
import { frequencyLabel } from './labels.js';
import type { DrillPlanSource } from './plan-source.js';
import type {
  DrillCompanyRule,
  DrillConduct,
  DrillPlan,
  DrillPlanner,
  DrillPostponement,
  DrillVariant,
} from './plan-types.js';
import { addMonths } from './plan-view.js';

type Errors = Record<string, string>;

function issuesOf(issues: readonly SchemaIssue[]): Errors {
  const out: Errors = {};
  for (const issue of issues) out[issue.path.split('.')[0] ?? ''] ??= issue.code;
  return out;
}

function useFieldError(errors: Errors) {
  const { t } = useTranslation();
  return (name: string) =>
    errors[name]
      ? t(`drillPlan.fieldErrors.${errors[name]}`, {
          defaultValue: t('drillPlan.fieldErrors.INVALID'),
        })
      : undefined;
}

function Facts({ items }: { items: [string, React.ReactNode][] }) {
  return (
    <dl className={work.facts}>
      {items.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Failure({ error }: { error: unknown }) {
  const errorText = useDrillErrorText();
  if (!error) return null;
  return (
    <p className={work.error} role="alert">
      {errorText(error)}
    </p>
  );
}

const lines = (value: string) =>
  value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

/* ---------- Plan ---------- */

/** Planı ekler ya da düzeltir (gemi): şablon, başlangıç, sorumlu zabit, katılımcılar, aralık. */
export function PlanDrawer({
  opened,
  data,
  plan,
  source,
  onClose,
  onSaved,
}: {
  opened: boolean;
  data: DrillPlanner;
  plan: DrillPlan | null;
  source: DrillPlanSource;
  onClose: () => void;
  onSaved: (plan: DrillPlan) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drillPlan.plan.${key}`);
  const today = data.vessels[0]?.today ?? '';
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [startOn, setStartOn] = useState<string | null>(null);
  const [responsible, setResponsible] = useState<string | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [interval, setInterval] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [draft, setDraft] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const fieldError = useFieldError(errors);
  useEffect(() => {
    if (!opened) return;
    setTemplateId(plan?.templateId ?? null);
    setStartOn(plan?.startOn ?? today);
    setResponsible(plan?.responsibleUserId ?? null);
    setRoles(plan?.participantRoles ?? []);
    setInterval(plan?.planIntervalMonths ?? '');
    setNotes(plan?.notes ?? '');
    setDraft(plan?.state === 'draft');
    setErrors({});
    setFailure(null);
  }, [opened, plan, today]);
  const planned = new Set(
    data.plans.filter((p) => p.state !== 'cancelled' && p.id !== plan?.id).map((p) => p.templateId),
  );
  const templates = data.templates
    .filter((tpl) => (tpl.active && !planned.has(tpl.id)) || tpl.id === plan?.templateId)
    .map((tpl) => ({ value: tpl.id, label: `${tpl.code} · ${tpl.name}` }));
  const template = data.templates.find((tpl) => tpl.id === templateId);
  const submit = async () => {
    const parsed = parseDrillPlan({
      templateId,
      startOn,
      responsibleUserId: responsible,
      participantRoles: roles,
      intervalMonths: interval === '' ? null : interval,
      notes: notes === '' ? null : notes,
      state: draft ? 'draft' : 'active',
    });
    if (!parsed.ok) {
      setErrors(issuesOf(parsed.issues));
      return;
    }
    setErrors({});
    setFailure(null);
    setSaving(true);
    try {
      onSaved(
        await (source.savePlan?.({
          ...(plan ? { id: plan.id, rowVersion: plan.rowVersion } : {}),
          plan: parsed.value,
        }) ?? Promise.reject(new Error('FORBIDDEN'))),
      );
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      closeButtonProps={{ 'aria-label': t('drills.close') }}
      title={plan ? k('editTitle') : k('newTitle')}
    >
      <Stack gap="sm">
        <Select
          label={k('template')}
          required
          searchable
          data={templates}
          value={templateId}
          disabled={plan !== null}
          onChange={(value) => {
            setTemplateId(value);
            const tpl = data.templates.find((x) => x.id === value);
            if (tpl && roles.length === 0) setRoles(tpl.roles);
          }}
          error={fieldError('templateId')}
          nothingFoundMessage={k('noTemplates')}
        />
        {template ? (
          <p className={classes.muted}>
            {frequencyLabel(t, template.frequency)}
            {template.ruleCode ? ` · ${template.ruleCode}` : ''}
          </p>
        ) : null}
        <DayInput
          label={k('startOn')}
          description={k('startOnHint')}
          required
          value={startOn}
          onChange={setStartOn}
          error={fieldError('startOn')}
        />
        <Select
          label={k('responsible')}
          searchable
          clearable
          clearButtonProps={{ 'aria-label': t('drillPlan.filters.clear') }}
          data={data.crew.map((c) => ({
            value: c.userId,
            label: c.roleName ? `${c.name} · ${c.roleName}` : c.name,
          }))}
          value={responsible}
          onChange={setResponsible}
          error={fieldError('responsibleUserId')}
        />
        <TagsInput
          label={k('participants')}
          description={k('participantsHint')}
          value={roles}
          onChange={setRoles}
          error={fieldError('participantRoles')}
        />
        <NumberInput
          label={k('interval')}
          description={k('intervalHint')}
          min={1}
          max={60}
          allowDecimal={false}
          value={interval}
          onChange={(value) => setInterval(typeof value === 'number' ? value : '')}
          error={fieldError('intervalMonths')}
        />
        <Textarea
          label={k('notes')}
          autosize
          minRows={2}
          value={notes}
          onChange={(event) => setNotes(event.currentTarget.value)}
        />
        <Switch
          label={k('draft')}
          description={k('draftHint')}
          checked={draft}
          onChange={(event) => setDraft(event.currentTarget.checked)}
        />
        <Failure error={failure} />
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('drillPlan.actions.cancel')}
          </Button>
          <Button loading={saving} onClick={() => void submit()}>
            {t('drillPlan.actions.save')}
          </Button>
        </div>
      </Stack>
    </Drawer>
  );
}

/** Planın ayrıntısı: vade, kural, tetik, uygulamalar ve ertelemeler; gemide işlemler. */
export function PlanDetailDrawer({
  plan,
  data,
  source,
  onClose,
  onEdit,
  onConduct,
  onPostpone,
  onCancelPlan,
  onDecide,
  onVariant,
}: {
  plan: DrillPlan | null;
  data: DrillPlanner;
  source: DrillPlanSource;
  onClose: () => void;
  onEdit: () => void;
  onConduct: () => void;
  onPostpone: () => void;
  onCancelPlan: () => void;
  onDecide: (
    subject:
      | { kind: 'conduct'; item: DrillConduct }
      | {
          kind: 'postponement';
          item: DrillPostponement;
        },
  ) => void;
  onVariant: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillPlan.detail.${key}`, values);
  const shell = useShell();
  if (!plan) return null;
  const ship = data.side === 'ship';
  const active = plan.state !== 'cancelled';
  const rule = plan.rule;
  // Execution Wizard kaydı (Faz 3 Ek) kayıt ekranında açılır ve orada karara bağlanır
  const records = '/operations/drills/records';
  const openRecord = (recordId: string) => shell?.navigate(`${records}?record=${recordId}`);
  const pendingExecuted = plan.conducts.find((c) => c.status === 'pending' && c.recordId);
  const pendingConduct = plan.conducts.find((c) => c.status === 'pending' && !c.recordId);
  const pendingPostponement = plan.postponements.find((p) => p.status === 'pending');
  const canDecide = data.can.approve || data.can.return;
  return (
    <Drawer
      opened
      onClose={onClose}
      position="right"
      size="xl"
      closeButtonProps={{ 'aria-label': t('drills.close') }}
      title={`${plan.templateCode} · ${plan.templateName}`}
    >
      <Stack gap="md">
        <div className={classes.actions}>
          <DrillStatusBadge status={plan.status} />
          {plan.variant ? <ReviewBadge value={plan.variant.status} /> : null}
        </div>
        {ship && active ? (
          <div className={classes.actions}>
            {shell && plan.state === 'active' && (plan.openRecordId || data.can.create) ? (
              <Button
                size="xs"
                onClick={() =>
                  plan.openRecordId
                    ? openRecord(plan.openRecordId)
                    : shell.navigate(`${records}?start=${plan.id}`)
                }
              >
                {t(
                  plan.openRecordId
                    ? 'drillPlan.actions.continueDrill'
                    : 'drillPlan.actions.conductDrill',
                )}
              </Button>
            ) : null}
            {data.can.create || data.can.submit ? (
              <Button
                size="xs"
                variant="default"
                onClick={onConduct}
                disabled={!source.recordConduct}
              >
                {t('drillPlan.actions.recordConduct')}
              </Button>
            ) : null}
            {data.can.submit && plan.state === 'active' ? (
              <Button size="xs" variant="default" onClick={onPostpone}>
                {t('drillPlan.actions.postpone')}
              </Button>
            ) : null}
            {data.can.edit ? (
              <Button size="xs" variant="default" onClick={onEdit}>
                {t('drillPlan.actions.edit')}
              </Button>
            ) : null}
            {data.can.create ? (
              <Button size="xs" variant="default" onClick={onVariant}>
                {t('drillPlan.actions.variant')}
              </Button>
            ) : null}
            {data.can.edit ? (
              <Button size="xs" variant="subtle" color="red" onClick={onCancelPlan}>
                {t('drillPlan.actions.cancelPlan')}
              </Button>
            ) : null}
          </div>
        ) : null}
        {ship && canDecide && (pendingConduct || pendingPostponement || pendingExecuted) ? (
          <Alert color="yellow" title={k('awaitingDecision')}>
            <div className={classes.actions}>
              {pendingExecuted?.recordId && shell ? (
                <Button size="xs" onClick={() => openRecord(pendingExecuted.recordId as string)}>
                  {k('decideRecord', {
                    no: pendingExecuted.recordNo,
                    date: formatDay(pendingExecuted.conductedOn),
                  })}
                </Button>
              ) : null}
              {pendingConduct ? (
                <Button
                  size="xs"
                  onClick={() => onDecide({ kind: 'conduct', item: pendingConduct })}
                >
                  {k('decideConduct', { date: formatDay(pendingConduct.conductedOn) })}
                </Button>
              ) : null}
              {pendingPostponement ? (
                <Button
                  size="xs"
                  onClick={() => onDecide({ kind: 'postponement', item: pendingPostponement })}
                >
                  {k('decidePostponement', { date: formatDay(pendingPostponement.newDueOn) })}
                </Button>
              ) : null}
            </div>
          </Alert>
        ) : null}
        <Facts
          items={[
            [k('vessel'), plan.vesselName ?? '—'],
            [k('category'), plan.categoryName ?? '—'],
            [
              k('frequency'),
              `${t('drillPlan.months', { count: plan.intervalMonths })} · ${t(
                `drillPlan.intervalSource.${plan.intervalSource}`,
              )}`,
            ],
            [
              k('rule'),
              rule
                ? `${rule.code} · ${rule.reference}${
                    rule.source === 'company' ? ` (${t('drillPlan.rules.company')})` : ''
                  }`
                : '—',
            ],
            [k('lastConducted'), formatDay(plan.lastConductedOn)],
            [
              k('nextDue'),
              `${formatDay(plan.due.nextDueOn)} · ${t(`drillPlan.basis.${plan.due.basis}`)}`,
            ],
            [k('originalDue'), formatDay(plan.due.originalDueOn)],
            [
              k('crewChange'),
              plan.trigger
                ? k('crewChangeValue', {
                    date: formatDay(plan.trigger.triggeredOn),
                    due: formatDay(plan.trigger.dueOn),
                    newcomers: plan.trigger.newcomers,
                    onBoard: plan.trigger.onBoard,
                  })
                : '—',
            ],
            [k('responsible'), plan.responsibleName ?? '—'],
            [k('participants'), plan.participantRoles.join(', ') || '—'],
            [k('startOn'), formatDay(plan.startOn)],
            [k('state'), t(`drillPlan.state.${plan.state}`)],
          ]}
        />
        {plan.cancelReason ? (
          <Alert color="gray" title={k('cancelled')}>
            {plan.cancelReason}
          </Alert>
        ) : null}
        {plan.notes ? <p className={classes.muted}>{plan.notes}</p> : null}
        <section>
          <h3 className={classes.sectionTitle}>{k('conducts')}</h3>
          {plan.conducts.length === 0 ? (
            <p className={classes.muted}>{k('noConducts')}</p>
          ) : (
            <div className={table.tableBox}>
              <table className={table.table} data-cards>
                <thead>
                  <tr>
                    <th scope="col">{k('date')}</th>
                    <th scope="col">{k('kind')}</th>
                    <th scope="col">{k('reference')}</th>
                    <th scope="col">{k('enteredBy')}</th>
                    <th scope="col">{k('decision')}</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.conducts.map((c) => (
                    <tr key={c.id}>
                      <td data-label={k('date')}>{formatDay(c.conductedOn)}</td>
                      <td data-label={k('kind')}>{t(`drillPlan.conductKind.${c.kind}`)}</td>
                      <td data-label={k('reference')}>
                        {c.recordId && shell ? (
                          <button
                            type="button"
                            className={classes.linkButton}
                            onClick={() => openRecord(c.recordId as string)}
                          >
                            {c.recordNo}
                          </button>
                        ) : (
                          (c.reference ?? c.notes ?? '—')
                        )}
                      </td>
                      <td data-label={k('enteredBy')}>{c.createdByName ?? '—'}</td>
                      <td data-label={k('decision')}>
                        <ReviewBadge value={c.status} />{' '}
                        {c.decision ? <DecisionNote decision={c.decision} /> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <section>
          <h3 className={classes.sectionTitle}>{k('postponements')}</h3>
          {plan.postponements.length === 0 ? (
            <p className={classes.muted}>{k('noPostponements')}</p>
          ) : (
            <PostponementTable items={plan.postponements} fleet={false} />
          )}
        </section>
      </Stack>
    </Drawer>
  );
}

function DecisionNote({ decision }: { decision: DrillConduct['decision'] }) {
  const { t } = useTranslation();
  if (!decision) return null;
  return (
    <span className={classes.muted}>
      {decision.decidedByName ?? '—'}
      {decision.capacity === 'deputy' ? ` (${t('drillPlan.onBehalf')})` : ''} ·{' '}
      {formatDateTime(decision.decidedAt)}
      {decision.reason ? ` · ${decision.reason}` : ''}
    </span>
  );
}

/** Ertelemeler: orijinal, önceki ve yeni vade; gerekçe; talep eden; kaptanın kararı; etki. */
export function PostponementTable({
  items,
  fleet,
}: {
  items: readonly DrillPostponement[];
  fleet: boolean;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drillPlan.postponements.${key}`);
  return (
    <div className={table.tableBox}>
      <table className={table.table} data-cards>
        <thead>
          <tr>
            {fleet ? <th scope="col">{k('vessel')}</th> : null}
            {fleet ? <th scope="col">{k('drill')}</th> : null}
            <th scope="col">{k('reason')}</th>
            <th scope="col">{k('requestedBy')}</th>
            <th scope="col">{k('decidedBy')}</th>
            <th scope="col">{k('originalDue')}</th>
            <th scope="col">{k('newDue')}</th>
            <th scope="col">{k('delay')}</th>
            <th scope="col">{k('status')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id}>
              {fleet ? <td data-label={k('vessel')}>{p.vesselName ?? '—'}</td> : null}
              {fleet ? (
                <td data-label={k('drill')}>
                  {p.templateCode} · {p.templateName}
                </td>
              ) : null}
              <td data-label={k('reason')}>{p.reason}</td>
              <td data-label={k('requestedBy')}>
                {p.requestedByName ?? '—'}
                <br />
                <span className={classes.muted}>{formatDateTime(p.requestedAt)}</span>
              </td>
              <td data-label={k('decidedBy')}>
                {p.decision ? <DecisionNote decision={p.decision} /> : '—'}
              </td>
              <td data-label={k('originalDue')}>{formatDay(p.firstOriginalDueOn)}</td>
              <td data-label={k('newDue')}>{formatDay(p.newDueOn)}</td>
              <td data-label={k('delay')} data-numeric>
                {t('drillPlan.days', { count: p.delayDays })}
              </td>
              <td data-label={k('status')}>
                <ReviewBadge value={p.effect} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Kayıtlı uygulama ---------- */

/**
 * Başlangıç kaydı ya da kayıtlı uygulama (ADR 0128 §1): tarih (gelecek tarih yok), referans,
 * not (başlangıç kaydında gerekçe zorunlu). Kaptan onaylayınca Last Conducted olur.
 */
export function ConductDialog({
  plan,
  today,
  source,
  onClose,
  onSaved,
}: {
  plan: DrillPlan | null;
  today: string;
  source: DrillPlanSource;
  onClose: () => void;
  onSaved: (plan: DrillPlan) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drillPlan.conduct.${key}`);
  const [kind, setKind] = useState<'baseline' | 'recorded'>('recorded');
  const [date, setDate] = useState<string | null>(today);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const fieldError = useFieldError(errors);
  useEffect(() => {
    if (!plan) return;
    setKind(plan.conducts.some((c) => c.status === 'approved') ? 'recorded' : 'baseline');
    setDate(today);
    setReference('');
    setNotes('');
    setErrors({});
    setFailure(null);
  }, [plan, today]);
  if (!plan) return null;
  const submit = async () => {
    const parsed = parseDrillConduct({
      planId: plan.id,
      conductedOn: date,
      kind,
      reference: reference === '' ? null : reference,
      notes: notes === '' ? null : notes,
    });
    if (!parsed.ok) {
      setErrors(issuesOf(parsed.issues));
      return;
    }
    if (parsed.value.conductedOn > today) {
      setErrors({ conductedOn: 'FUTURE_DATE' });
      return;
    }
    setSaving(true);
    setFailure(null);
    try {
      const saved = await source.recordConduct?.({
        planId: plan.id,
        conductedOn: parsed.value.conductedOn,
        kind: parsed.value.kind,
        reference: parsed.value.reference,
        notes: parsed.value.notes,
      });
      if (saved) onSaved(saved);
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      closeButtonProps={{ 'aria-label': t('drills.close') }}
      title={`${k('title')} · ${plan.templateCode}`}
    >
      <Stack gap="sm">
        <SegmentedControl
          value={kind}
          onChange={(value) => setKind(value === 'baseline' ? 'baseline' : 'recorded')}
          data={[
            { value: 'recorded', label: t('drillPlan.conductKind.recorded') },
            { value: 'baseline', label: t('drillPlan.conductKind.baseline') },
          ]}
          aria-label={k('kind')}
        />
        <p className={classes.muted}>{k(kind === 'baseline' ? 'baselineHint' : 'recordedHint')}</p>
        <DayInput
          label={k('date')}
          required
          maxDate={today}
          value={date}
          onChange={setDate}
          error={fieldError('conductedOn')}
        />
        <TextInput
          label={k('reference')}
          value={reference}
          onChange={(event) => setReference(event.currentTarget.value)}
          error={fieldError('reference')}
        />
        <Textarea
          label={k(kind === 'baseline' ? 'reason' : 'notes')}
          required={kind === 'baseline'}
          autosize
          minRows={2}
          value={notes}
          onChange={(event) => setNotes(event.currentTarget.value)}
          error={fieldError('notes')}
        />
        <Failure error={failure} />
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('drillPlan.actions.cancel')}
          </Button>
          <Button loading={saving} onClick={() => void submit()}>
            {k('submit')}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

/* ---------- Erteleme ---------- */

/**
 * Erteleme penceresi (PMS ertelemesiyle aynı dil; ADR 0108): güncel ve orijinal vade, en geç
 * yeni vade (bir aralık), gerekçe. Talep kaptan onayına gider; vade onaylanınca değişir.
 */
export function PostponeDialog({
  plan,
  today,
  source,
  onClose,
  onSaved,
}: {
  plan: DrillPlan | null;
  today: string;
  source: DrillPlanSource;
  onClose: () => void;
  onSaved: (plan: DrillPlan) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillPlan.postpone.${key}`, values);
  const [date, setDate] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const fieldError = useFieldError(errors);
  // biome-ignore lint/correctness/useExhaustiveDependencies: yeni kayıtla açılınca sıfırlanır
  useEffect(() => {
    setDate(null);
    setReason('');
    setErrors({});
    setFailure(null);
  }, [plan]);
  if (!plan) return null;
  const current = plan.due.periodicDueOn;
  const latest = addMonths(plan.due.originalDueOn, plan.intervalMonths);
  const submit = async () => {
    const parsed = parseDrillPostponement({ planId: plan.id, newDueOn: date, reason });
    if (!parsed.ok) {
      setErrors(issuesOf(parsed.issues));
      return;
    }
    const next = parsed.value.newDueOn;
    const local: Errors = {};
    if (next <= current) local.newDueOn = 'POSTPONEMENT_MUST_BE_LATER';
    else if (next < today) local.newDueOn = 'DUE_IN_PAST';
    else if (next > latest) local.newDueOn = 'POSTPONEMENT_TOO_LONG';
    if (Object.keys(local).length > 0) {
      setErrors(local);
      return;
    }
    setSaving(true);
    setFailure(null);
    try {
      const saved = await source.requestPostponement?.(parsed.value);
      if (saved) onSaved(saved);
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      closeButtonProps={{ 'aria-label': t('drills.close') }}
      title={k('title', { code: plan.templateCode })}
    >
      <Stack gap="sm">
        <Facts
          items={[
            [k('drill'), `${plan.templateCode} · ${plan.templateName}`],
            [k('currentDue'), formatDay(current)],
            [k('originalDue'), formatDay(plan.due.originalDueOn)],
            [k('latest'), formatDay(latest)],
          ]}
        />
        <DayInput
          label={k('newDue')}
          required
          minDate={today}
          maxDate={latest}
          value={date}
          onChange={setDate}
          error={fieldError('newDueOn')}
        />
        <Textarea
          label={k('reason')}
          required
          autosize
          minRows={3}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
          error={fieldError('reason')}
        />
        <p className={classes.muted}>{k('note')}</p>
        <Failure error={failure} />
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('drillPlan.actions.cancel')}
          </Button>
          <Button loading={saving} onClick={() => void submit()}>
            {k('submit')}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

/* ---------- Kaptan kararı ---------- */

export type DecisionSubject =
  | { kind: 'conduct'; item: DrillConduct }
  | { kind: 'postponement'; item: DrillPostponement };

/**
 * Kaptanın (ya da vekilinin; ADR 0127) kararı: onay ya da gerekçeli iade; karar gördüğü içeriğin
 * hash'ini taşır. Parola yalnız şirket ayarı açıkken (ADR 0075).
 */
export function DecisionDialog({
  plan,
  subject,
  data,
  source,
  onClose,
  onSaved,
}: {
  plan: DrillPlan | null;
  subject: DecisionSubject | null;
  data: DrillPlanner;
  source: DrillPlanSource;
  onClose: () => void;
  onSaved: (plan: DrillPlan) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillPlan.decision.${key}`, values);
  const reauth = useReauth('drill_decision');
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<unknown>(null);
  const [saving, setSaving] = useState<'approved' | 'returned' | null>(null);
  const [missingReason, setMissingReason] = useState(false);
  const resetPassword = reauth.reset;
  // biome-ignore lint/correctness/useExhaustiveDependencies: yeni kayıtla açılınca sıfırlanır
  useEffect(() => {
    setReason('');
    setFailure(null);
    setMissingReason(false);
    resetPassword();
  }, [subject, resetPassword]);
  if (!plan || !subject) return null;
  const decide = async (decision: 'approved' | 'returned') => {
    if (decision === 'returned' && reason.trim() === '') {
      setMissingReason(true);
      return;
    }
    if (reauth.missing) return;
    setSaving(decision);
    setFailure(null);
    try {
      const saved = await source.decide?.({
        decision: {
          subject: subject.kind,
          id: subject.item.id,
          decision,
          reason: reason.trim() === '' ? null : reason.trim(),
          contentHash: subject.item.contentHash,
        },
        ...reauth.body(),
      });
      if (saved) onSaved(saved);
    } catch (error) {
      reauth.noteError(error);
      setFailure(error);
    } finally {
      setSaving(null);
    }
  };
  const facts: [string, React.ReactNode][] =
    subject.kind === 'conduct'
      ? [
          [k('drill'), `${plan.templateCode} · ${plan.templateName}`],
          [k('date'), formatDay(subject.item.conductedOn)],
          [k('kind'), t(`drillPlan.conductKind.${subject.item.kind}`)],
          [k('enteredBy'), subject.item.createdByName ?? '—'],
          [k('reference'), subject.item.reference ?? '—'],
          [k('notes'), subject.item.notes ?? '—'],
        ]
      : [
          [k('drill'), `${plan.templateCode} · ${plan.templateName}`],
          [k('originalDue'), formatDay(subject.item.firstOriginalDueOn)],
          [k('currentDue'), formatDay(subject.item.previousDueOn)],
          [k('newDue'), formatDay(subject.item.newDueOn)],
          [k('requestedBy'), subject.item.requestedByName ?? '—'],
          [k('reason'), subject.item.reason],
        ];
  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      closeButtonProps={{ 'aria-label': t('drills.close') }}
      title={k(subject.kind === 'conduct' ? 'conductTitle' : 'postponementTitle')}
    >
      <Stack gap="sm">
        <Facts items={facts} />
        {data.capacity === 'deputy' ? (
          <Alert color="blue">{t('drillPlan.onBehalfNote')}</Alert>
        ) : null}
        <Textarea
          label={k('comment')}
          description={k('commentHint')}
          autosize
          minRows={2}
          value={reason}
          onChange={(event) => {
            setReason(event.currentTarget.value);
            setMissingReason(false);
          }}
          error={missingReason ? t('drillPlan.fieldErrors.REQUIRED') : undefined}
        />
        <ReauthField reauth={reauth} label={k('password')} />
        <Failure error={failure} />
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('drillPlan.actions.cancel')}
          </Button>
          {data.can.return ? (
            <Button
              color="red"
              variant="light"
              loading={saving === 'returned'}
              disabled={reauth.missing}
              onClick={() => void decide('returned')}
            >
              {k('return')}
            </Button>
          ) : null}
          {data.can.approve ? (
            <Button
              loading={saving === 'approved'}
              disabled={reauth.missing}
              onClick={() => void decide('approved')}
            >
              {k('approve')}
            </Button>
          ) : null}
        </div>
      </Stack>
    </Modal>
  );
}

/** Planı gerekçeyle iptal eder (geri alınmaz). */
export function CancelPlanDialog({
  plan,
  source,
  onClose,
  onSaved,
}: {
  plan: DrillPlan | null;
  source: DrillPlanSource;
  onClose: () => void;
  onSaved: (plan: DrillPlan) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<unknown>(null);
  const [missing, setMissing] = useState(false);
  const [saving, setSaving] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: yeni kayıtla açılınca sıfırlanır
  useEffect(() => {
    setReason('');
    setFailure(null);
    setMissing(false);
  }, [plan]);
  if (!plan) return null;
  const submit = async () => {
    if (reason.trim() === '') {
      setMissing(true);
      return;
    }
    setSaving(true);
    try {
      const saved = await source.cancelPlan?.({
        id: plan.id,
        rowVersion: plan.rowVersion,
        reason: reason.trim(),
      });
      if (saved) onSaved(saved);
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      opened
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('drills.close') }}
      title={t('drillPlan.cancelPlan.title', { code: plan.templateCode })}
    >
      <Stack gap="sm">
        <p className={classes.muted}>{t('drillPlan.cancelPlan.note')}</p>
        <Textarea
          label={t('drillPlan.cancelPlan.reason')}
          required
          autosize
          minRows={2}
          value={reason}
          onChange={(event) => {
            setReason(event.currentTarget.value);
            setMissing(false);
          }}
          error={missing ? t('drillPlan.fieldErrors.REQUIRED') : undefined}
        />
        <Failure error={failure} />
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('drillPlan.actions.back')}
          </Button>
          <Button color="red" loading={saving} onClick={() => void submit()}>
            {t('drillPlan.cancelPlan.submit')}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

/* ---------- Gemiye özel varyant ---------- */

/**
 * Gemiye özel senaryo (§13.5): amaç, senaryo, adımlar ve ekipman gemiye uyarlanır; merkez şablon
 * değişmez. Taslak kaydedilir ya da ofis onayına gönderilir.
 */
export function VariantDrawer({
  plan,
  data,
  source,
  onClose,
  onSaved,
}: {
  plan: DrillPlan | null;
  data: DrillPlanner;
  source: DrillPlanSource;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drillPlan.variant.${key}`);
  const template = data.templates.find((tpl) => tpl.id === plan?.templateId);
  const existing = plan?.variant ?? null;
  const [objective, setObjective] = useState('');
  const [scenario, setScenario] = useState('');
  const [steps, setSteps] = useState('');
  const [equipment, setEquipment] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<unknown>(null);
  const [saving, setSaving] = useState<'draft' | 'submit' | null>(null);
  const fieldError = useFieldError(errors);
  useEffect(() => {
    const base: DrillTemplateContent | undefined = existing?.content ?? template?.content;
    setObjective(base?.objective ?? '');
    setScenario(base?.scenario ?? '');
    setSteps((base?.steps ?? []).join('\n'));
    setEquipment((base?.equipment ?? []).join('\n'));
    setReason(existing?.reason ?? '');
    setErrors({});
    setFailure(null);
  }, [existing, template]);
  if (!plan || !template) return null;
  const save = async (submit: boolean) => {
    const content = {
      ...(existing?.content ?? template.content),
      objective,
      scenario,
      steps: lines(steps),
      equipment: lines(equipment),
    };
    const parsed = parseDrillVariant({ templateId: template.id, content, reason });
    if (!parsed.ok) {
      setErrors(issuesOf(parsed.issues));
      return;
    }
    setSaving(submit ? 'submit' : 'draft');
    setFailure(null);
    try {
      await source.saveVariant?.({
        ...(existing ? { id: existing.id, rowVersion: existing.rowVersion } : {}),
        variant: {
          templateId: template.id,
          content: parsed.value.content,
          reason: parsed.value.reason,
        },
        submit,
      });
      onSaved();
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(null);
    }
  };
  return (
    <Drawer
      opened
      onClose={onClose}
      position="right"
      size="xl"
      closeButtonProps={{ 'aria-label': t('drills.close') }}
      title={`${k('title')} · ${template.code}`}
    >
      <Stack gap="sm">
        <p className={classes.muted}>{k('note')}</p>
        {existing ? (
          <div className={classes.actions}>
            <ReviewBadge value={existing.status} />
            {existing.decisions[0]?.reason ? (
              <span className={classes.muted}>{existing.decisions[0].reason}</span>
            ) : null}
          </div>
        ) : null}
        <Textarea
          label={t('drills.fields.objective')}
          autosize
          minRows={2}
          value={objective}
          onChange={(event) => setObjective(event.currentTarget.value)}
          error={fieldError('content')}
        />
        <Textarea
          label={t('drills.fields.scenario')}
          autosize
          minRows={3}
          value={scenario}
          onChange={(event) => setScenario(event.currentTarget.value)}
        />
        <Textarea
          label={t('drills.fields.steps')}
          description={k('linesHint')}
          autosize
          minRows={4}
          value={steps}
          onChange={(event) => setSteps(event.currentTarget.value)}
        />
        <Textarea
          label={t('drills.fields.equipment')}
          description={k('linesHint')}
          autosize
          minRows={2}
          value={equipment}
          onChange={(event) => setEquipment(event.currentTarget.value)}
        />
        <Textarea
          label={k('reason')}
          required
          autosize
          minRows={2}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
          error={fieldError('reason')}
        />
        <Failure error={failure} />
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('drillPlan.actions.cancel')}
          </Button>
          <Button variant="default" loading={saving === 'draft'} onClick={() => void save(false)}>
            {k('saveDraft')}
          </Button>
          <Button loading={saving === 'submit'} onClick={() => void save(true)}>
            {k('submit')}
          </Button>
        </div>
      </Stack>
    </Drawer>
  );
}

/** Ofisin varyant kararı: merkez şablonla yan yana; onay ya da gerekçeli iade. */
export function VariantDecisionDialog({
  variant,
  source,
  onClose,
  onSaved,
}: {
  variant: DrillVariant | null;
  source: DrillPlanSource;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drillPlan.variant.${key}`);
  const reauth = useReauth('drill_variant_decision');
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const resetPassword = reauth.reset;
  // biome-ignore lint/correctness/useExhaustiveDependencies: yeni kayıtla açılınca sıfırlanır
  useEffect(() => {
    setReason('');
    setMissing(false);
    setFailure(null);
    resetPassword();
  }, [variant, resetPassword]);
  if (!variant) return null;
  const decide = async (decision: 'approved' | 'returned') => {
    if (decision === 'returned' && reason.trim() === '') {
      setMissing(true);
      return;
    }
    if (reauth.missing) return;
    setSaving(decision);
    setFailure(null);
    try {
      await source.decideVariant?.({
        id: variant.id,
        decision: {
          decision,
          reason: reason.trim() === '' ? null : reason.trim(),
          contentHash: variant.contentHash,
        },
        ...reauth.body(),
      });
      onSaved();
    } catch (error) {
      reauth.noteError(error);
      setFailure(error);
    } finally {
      setSaving(null);
    }
  };
  const side = (title: string, content: DrillTemplateContent | undefined) => (
    <section>
      <h3 className={classes.sectionTitle}>{title}</h3>
      <p className={classes.muted}>{content?.scenario ?? '—'}</p>
      <ol className={classes.steps}>
        {(content?.steps ?? []).map((step, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: adımlar sıralı metindir
          <li key={index}>{step}</li>
        ))}
      </ol>
    </section>
  );
  return (
    <Modal
      opened
      onClose={onClose}
      size="xl"
      closeButtonProps={{ 'aria-label': t('drills.close') }}
      title={`${k('decisionTitle')} · ${variant.templateCode ?? ''}`}
    >
      <Stack gap="sm">
        <Facts
          items={[
            [k('vessel'), variant.vesselName ?? '—'],
            [k('baseVersion'), `v${variant.baseVersion}`],
            [k('reason'), variant.reason],
            [k('by'), variant.createdByName ?? '—'],
          ]}
        />
        <div className={classes.compare}>
          {side(k('central'), variant.templateContent)}
          {side(k('proposed'), variant.content)}
        </div>
        {variant.status === 'pending' ? (
          <>
            <Textarea
              label={t('drillPlan.decision.comment')}
              autosize
              minRows={2}
              value={reason}
              onChange={(event) => {
                setReason(event.currentTarget.value);
                setMissing(false);
              }}
              error={missing ? t('drillPlan.fieldErrors.REQUIRED') : undefined}
            />
            <ReauthField reauth={reauth} label={t('drillPlan.decision.password')} />
          </>
        ) : (
          <ReviewBadge value={variant.status} />
        )}
        <Failure error={failure} />
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('drillPlan.actions.back')}
          </Button>
          {variant.status === 'pending' ? (
            <>
              <Button
                color="red"
                variant="light"
                loading={saving === 'returned'}
                disabled={reauth.missing}
                onClick={() => void decide('returned')}
              >
                {t('drillPlan.decision.return')}
              </Button>
              <Button
                loading={saving === 'approved'}
                disabled={reauth.missing}
                onClick={() => void decide('approved')}
              >
                {t('drillPlan.decision.approve')}
              </Button>
            </>
          ) : null}
        </div>
      </Stack>
    </Modal>
  );
}

/* ---------- Şirket kural seti ---------- */

/**
 * Şirket kural satırı (ADR 0128 §4): varsayılan kurala gemi tipi / bayrak koşullu şirket değeri.
 * SOLAS ve ISPS yalnız sıkılaştırılır; muafiyet "uygulanmaz" ve gerekçeyle.
 */
export function RuleDrawer({
  opened,
  rule,
  source,
  onClose,
  onSaved,
}: {
  opened: boolean;
  rule: DrillCompanyRule | null;
  source: DrillPlanSource;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drillPlan.rules.${key}`);
  const [code, setCode] = useState<string | null>(null);
  const [types, setTypes] = useState<string[]>([]);
  const [flags, setFlags] = useState<string[]>([]);
  const [value, setValue] = useState<number | ''>('');
  const [applicable, setApplicable] = useState(true);
  const [active, setActive] = useState(true);
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const fieldError = useFieldError(errors);
  useEffect(() => {
    if (!opened) return;
    setCode(rule?.code ?? null);
    setTypes(rule?.vesselTypes ?? []);
    setFlags(rule?.flags ?? []);
    setValue(rule?.intervalMonths ?? rule?.triggerHours ?? '');
    setApplicable(rule?.applicable ?? true);
    setActive(rule?.active ?? true);
    setReason(rule?.reason ?? '');
    setErrors({});
    setFailure(null);
  }, [opened, rule]);
  const base = DEFAULT_DRILL_RULES.find((r) => r.code === code);
  const periodic = base ? base.intervalMonths !== null : true;
  const submit = async () => {
    const number = value === '' ? null : value;
    const parsed = parseCompanyDrillRule({
      code,
      vesselTypes: types,
      flags,
      intervalMonths: periodic ? number : null,
      triggerHours: periodic ? null : number,
      applicable,
      reason,
      active,
    });
    if (!parsed.ok) {
      setErrors(issuesOf(parsed.issues));
      return;
    }
    setSaving(true);
    setFailure(null);
    try {
      await source.saveRule?.({
        ...(rule ? { id: rule.id, rowVersion: rule.rowVersion } : {}),
        rule: parsed.value,
      });
      onSaved();
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      closeButtonProps={{ 'aria-label': t('drills.close') }}
      title={rule ? k('editTitle') : k('newTitle')}
    >
      <Stack gap="sm">
        <Select
          label={k('code')}
          required
          searchable
          disabled={rule !== null}
          data={DEFAULT_DRILL_RULES.map((r) => ({
            value: r.code,
            label: `${r.code} · ${r.title}`,
          }))}
          value={code}
          onChange={setCode}
          error={fieldError('code')}
        />
        {base ? (
          <p className={classes.muted}>
            {base.reference} ·{' '}
            {base.intervalMonths !== null
              ? t('drillPlan.months', { count: base.intervalMonths })
              : t('drillPlan.hours', { count: base.triggerHours ?? 0 })}{' '}
            · {t(`drillPlan.rules.basis.${base.basis}`)}
          </p>
        ) : null}
        <TagsInput
          label={k('vesselTypes')}
          description={k('vesselTypesHint')}
          value={types}
          onChange={setTypes}
          error={fieldError('vesselTypes')}
        />
        <TagsInput
          label={k('flags')}
          description={k('flagsHint')}
          value={flags}
          onChange={setFlags}
          error={fieldError('flags')}
        />
        <Switch
          label={k('applicable')}
          description={k('applicableHint')}
          checked={applicable}
          onChange={(event) => setApplicable(event.currentTarget.checked)}
        />
        {applicable ? (
          <NumberInput
            label={periodic ? k('intervalMonths') : k('triggerHours')}
            required
            min={1}
            max={periodic ? 60 : 720}
            allowDecimal={false}
            value={value}
            onChange={(next) => setValue(typeof next === 'number' ? next : '')}
            error={fieldError(periodic ? 'intervalMonths' : 'triggerHours')}
          />
        ) : null}
        <Textarea
          label={k('reason')}
          description={k('reasonHint')}
          required
          autosize
          minRows={2}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
          error={fieldError('reason')}
        />
        <Switch
          label={k('active')}
          checked={active}
          onChange={(event) => setActive(event.currentTarget.checked)}
        />
        <Failure error={failure} />
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('drillPlan.actions.cancel')}
          </Button>
          <Button loading={saving} onClick={() => void submit()}>
            {t('drillPlan.actions.save')}
          </Button>
        </div>
      </Stack>
    </Drawer>
  );
}
